import React, { useState, useEffect } from 'react';
import { Search, Download, Filter, MoreHorizontal, Plus, ChevronDown, User, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useOrganization } from '@clerk/clerk-react';
import { useMembersApi } from '@/features/members';

const getStatusBadge = (status) => {
  const variants = {
    Active: 'bg-green-100 text-green-800 hover:bg-green-100',
    Inactive: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    Suspended: 'bg-red-100 text-red-800 hover:bg-red-100',
  };

  return variants[status] || variants.Active;
};

export default function Teachers() {
  const { organization, invitations } = useOrganization({
    invitations: {
      pageSize: 50,
      keepPreviousData: true,
      status: ['pending'],
    },
  });

  const { getMembers, addMember } = useMembersApi();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    uid: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  useEffect(() => {
    const fetchTeachers = async () => {
      if (!organization?.id) return;

      try {
        setLoading(true);
        const apiTeachers = await getMembers({ role: 'staff' });
        setTeachers(apiTeachers);
        setError(null);
      } catch (err) {
        console.error('Error fetching teachers:', err);
        setError('Failed to fetch teachers data');
        setTeachers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, [organization?.id]);

  const handleTeacherSelection = (teacherId) => {
    setSelectedTeachers((prev) =>
      prev.includes(teacherId) ? prev.filter((id) => id !== teacherId) : [...prev, teacherId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTeachers.length === filteredTeachers.length) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(filteredTeachers.map((teacher) => teacher.uid));
    }
  };

  const addTeachersAPI = async (staffData) => {
    try {
      const result = await addMember(staffData.map((staff) => ({ ...staff, role: 'staff' })));
      return {
        success: true,
        data: result.data,
        message: result.message || 'Teachers added successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error,
        message: error.response?.data?.message || error.message || 'Failed to add teachers',
      };
    }
  };

  const filteredTeachers = teachers.filter((teacher) => {
    const matchesSearch =
      (teacher.name && teacher.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (teacher.email && teacher.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (teacher.uid && teacher.uid.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (teacher.class && teacher.class.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'All' || teacher.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const pendingInvitations = invitations?.data?.filter((invitation) => {
    // Only show pending invitations for staff/teacher roles
    const isStaffRole = invitation.role === 'org:staff' || invitation.role === 'staff';
    // Only show pending invitations for people who are NOT already active members
    const isAlreadyMember = teachers.some(
      (teacher) => teacher.email.toLowerCase() === invitation.emailAddress.toLowerCase()
    );
    return invitation.status === 'pending' && isStaffRole && !isAlreadyMember;
  }) || [];

  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // UID validation
    if (!formData.uid) {
      errors.uid = 'UID is required';
    } else if (formData.uid.length < 3) {
      errors.uid = 'UID must be at least 3 characters long';
    } else if (!/^[A-Z0-9]+$/.test(formData.uid)) {
      errors.uid = 'UID must contain only uppercase letters and numbers';
    }

    // Check if UID already exists
    if (formData.uid && teachers.some((teacher) => teacher.uid === formData.uid)) {
      errors.uid = 'This UID already exists';
    }

    // Check if email already exists
    if (formData.email && teachers.some((teacher) => teacher.email === formData.email)) {
      errors.email = 'This email is already registered';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const teachersData = [
        {
          email: formData.email,
          uid: formData.uid,
        },
      ];

      const result = await addTeachersAPI(teachersData);

      if (result.success) {
        // Reset form and close modal
        setFormData({ email: '', uid: '' });
        setFormErrors({});
        setShowAddTeacher(false);

        // Show success message
        alert('Teacher invitation sent successfully!');

        // Refresh the teachers list
        window.location.reload(); // Simple refresh, you could implement a more elegant solution
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error adding teacher:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddTeacher(false);
    setFormData({ email: '', uid: '' });
    setFormErrors({});
  };

  return (
    <div className="bg-background p-6 h-screen flex flex-col overflow-hidden">
      <div>
        <div className="md:flex space-y-4 items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground">All Teachers</h1>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Bulk upload
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setShowBulkUpload(true)}>Upload CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowBulkUpload(true)}>Upload Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button className="gap-2" onClick={() => setShowAddTeacher(true)}>
              <Plus className="h-4 w-4" />
              Add teachers
            </Button>
          </div>
        </div>

        <div className="md:flex space-y-4 items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for a teacher"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                disabled={loading}
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {loading ? 'Loading...' : `${filteredTeachers.length} teachers`}
              {pendingInvitations.length > 0 && (
                <span className="ml-2 text-blue-600">• {pendingInvitations.length} pending invitations</span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  Status: {statusFilter}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter('All')}>All</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Active')}>Active</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Inactive')}>Inactive</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Suspended')}>Suspended</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>All Classes</DropdownMenuItem>
                <DropdownMenuItem>Grade A</DropdownMenuItem>
                <DropdownMenuItem>Grade B</DropdownMenuItem>
                <DropdownMenuItem>Grade C</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div className="flex rounded-t-md bg-muted/40 hover:bg-muted/50 shrink-0 border border-b-0">
        <div className="px-2 py-3 w-10 text-left text-sm font-medium">
          <Checkbox
            className="rounded"
            checked={selectedTeachers.length === filteredTeachers.length && filteredTeachers.length > 0}
            onCheckedChange={handleSelectAll}
            disabled={loading}
          />
        </div>
        <div className="px-2 py-3 w-1/4 text-left text-sm font-medium">Teacher</div>
        <div className="px-2 py-3 w-1/5 text-left text-sm font-medium">UID</div>
        <div className="px-2 py-3 w-1/4 text-left text-sm font-medium">Email</div>
        <div className="px-2 py-3 w-1/6 text-left text-sm font-medium">Class</div>
        <div className="px-2 py-3 w-16 text-left text-sm font-medium">Status</div>
        <div className="px-2 py-3 w-16 text-left text-sm font-medium"></div>
      </div>

      {/* Table Body */}
      <ScrollArea className="relative rounded-b-md flex-grow border">
        <div className="divide-y divide-border">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading teachers...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <span className="text-red-500">{error}</span>
            </div>
          ) : filteredTeachers.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <span className="text-muted-foreground">No teachers found</span>
            </div>
          ) : (
            <>
              {pendingInvitations.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-blue-50 border-b">
                    <h3 className="text-sm font-medium text-blue-900">
                      Pending Invitations ({pendingInvitations.length})
                    </h3>
                  </div>
                  {pendingInvitations.map((invitation, idx) => (
                    <div
                      key={invitation.id}
                      className={`flex items-center hover:bg-muted/50 transition-colors ${
                        idx % 2 !== 0 ? 'bg-muted/20' : ''
                      }`}>
                      <div className="px-2 py-4 w-10"></div>
                      <div className="px-2 py-4 w-1/4 font-medium">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-blue-600" />
                          </div>
                          <span>{invitation.emailAddress}</span>
                        </div>
                      </div>
                      <div className="px-2 py-4 w-1/5 text-muted-foreground font-mono text-sm">PENDING</div>
                      <div className="px-2 py-4 w-1/4 text-muted-foreground">{invitation.emailAddress}</div>
                      <div className="px-2 py-4 w-1/6 text-muted-foreground">Not Assigned</div>
                      <div className="px-2 py-4 w-16">
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                          Invited
                        </Badge>
                      </div>
                      <div className="px-2 py-4 w-16">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-left">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>Resend invitation</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-500">Cancel invitation</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {teachers.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-50 border-b">
                    <h3 className="text-sm font-medium text-gray-900">Active Teachers ({filteredTeachers.length})</h3>
                  </div>
                  {filteredTeachers.map((teacher, idx) => (
                    <div
                      key={teacher.uid}
                      className={`flex items-center hover:bg-muted/50 transition-colors ${
                        idx % 2 !== 0 ? 'bg-muted/20' : ''
                      }`}>
                      <div className="px-2 py-4 w-10">
                        <Checkbox
                          className="rounded"
                          checked={selectedTeachers.includes(teacher.uid)}
                          onCheckedChange={() => handleTeacherSelection(teacher.uid)}
                        />
                      </div>
                      <div className="px-2 py-4 w-1/4 font-medium">
                        <div className="flex items-center gap-3">
                          {teacher.avatar ? (
                            <img
                              src={teacher.avatar}
                              alt={teacher.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <span>{teacher.name}</span>
                        </div>
                      </div>
                      <div className="px-2 py-4 w-1/5 text-muted-foreground font-mono text-sm">{teacher.uid}</div>
                      <div className="px-2 py-4 w-1/4 text-muted-foreground">{teacher.email}</div>
                      <div className="px-2 py-4 w-1/6 text-muted-foreground">{teacher.class || 'Not Assigned'}</div>
                      <div className="px-2 py-4 w-16">
                        <Badge variant="secondary" className={`${getStatusBadge(teacher.status)} text-xs`}>
                          {teacher.status}
                        </Badge>
                      </div>
                      <div className="px-2 py-4 w-16">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-left">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>View profile</DropdownMenuItem>
                            <DropdownMenuItem>Edit teacher</DropdownMenuItem>
                            <div className="border-t my-1" />
                            <DropdownMenuItem>Reset password</DropdownMenuItem>
                            <DropdownMenuItem>Send invitation</DropdownMenuItem>
                            <div className="border-t my-1" />
                            <DropdownMenuItem>Assign class</DropdownMenuItem>
                            <div className="border-t my-1" />
                            <DropdownMenuItem className="text-red-500">
                              {teacher.status === 'Suspended' ? 'Remove teacher' : 'Suspend teacher'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Add Teacher Modal */}
      <Dialog open={showAddTeacher} onOpenChange={setShowAddTeacher}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Teacher</DialogTitle>
            <DialogDescription>
              Enter the teacher's email and UID to send them an invitation to join the organization.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddTeacher} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="teacher@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={formErrors.email ? 'border-red-500' : ''}
                required
              />
              {formErrors.email && <p className="text-sm text-red-500">{formErrors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="uid">Teacher UID *</Label>
              <Input
                id="uid"
                type="text"
                placeholder="TC001"
                value={formData.uid}
                onChange={(e) => handleInputChange('uid', e.target.value.toUpperCase())}
                className={formErrors.uid ? 'border-red-500' : ''}
                maxLength={10}
                required
              />
              {formErrors.uid && <p className="text-sm text-red-500">{formErrors.uid}</p>}
              <p className="text-xs text-muted-foreground">
                UID must be unique and contain only uppercase letters and numbers
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending invitation...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
